import { useContext } from "react";

import { ShipperAddress } from "api/rest/address/types";
import { Response, useUpdateAddressMutation } from "api/rest/address/updateAddressApi";
import { ShipperQuoteRequest, Truck, useUpdateShipperQuoteRequestMutation } from "api/rest/quote-requests";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import merge from "lodash/merge";
import set from "lodash/set";

import { useRequestManagementSlices, useRequestManagementSliceSelector } from "../../store/requestManagementStore";
import { updateQuoteRequestActions } from "../../store/updateQuoteRequestSlice";
import useLoadQuoteRequestAndQuote from "./useLoadQuoteRequestAndQuote";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useEditableQuoteRequest = () => {
  useRequestManagementSlices();

  const { isLoading, quoteRequest, quote } = useLoadQuoteRequestAndQuote();
  const { quoteRequest: quoteRequestUpdates, isEditing } = useRequestManagementSliceSelector(
    (store) => store.updateQuoteRequestSlice
  );
  const { useResetState, useSetState, useSetQuoteRequest, useSetQuoteRequestByPath } = updateQuoteRequestActions;
  const setState = useSetState();
  const resetState = useResetState();
  const { save: saveCargoGroups } = useContext(CargoGroupContext);

  const [updateQuoteRequestMutation] = useUpdateShipperQuoteRequestMutation();
  const [updateAddress] = useUpdateAddressMutation();

  const setEditing = (isEditing: boolean) => {
    if (isEditing) {
      setState({ isEditing: true });
      return;
    }

    resetState();
  };

  const updateQuoteRequest = async () => {
    if (!quoteRequest) {
      return;
    }

    if (quoteRequest.cargo_groups) {
      await saveCargoGroups();
    }

    // We only need to update addresses if they are changed and not new
    // The QR update endpoint will handle new addresses
    const changedAddresses = quoteRequestUpdates.stops
      ?.map((stop) => stop?.address)
      .filter((maybeStopAddress) => !!maybeStopAddress && maybeStopAddress.id);

    let updatedAddresses: Response[] | undefined;
    if (!!changedAddresses?.length) {
      updatedAddresses = await Promise.all(
        changedAddresses.map((addressChanges) =>
          updateAddress({
            body: (addressChanges ?? {}) as Partial<ShipperAddress>,
            urlParams: { addressId: addressChanges?.id as number },
          }).unwrap()
        )
      );
    }

    await updateQuoteRequestMutation({
      body: {
        ...quoteRequestUpdates,
        // Because of how the QR update endpoint handles arrays we will just pass up all the stops again on change.
        ...(!!quoteRequestUpdates.stops?.length
          ? {
              stops: merge(
                [],
                quoteRequest.stops,
                quoteRequestUpdates.stops.map((updatedStop) => ({
                  ...updatedStop,
                  address: updatedAddresses?.some(
                    (updatedAddress) => updatedAddress.data.address.canonical_id === updatedStop?.address?.canonical_id
                  )
                    ? {
                        ...updatedStop?.address,
                        id:
                          updatedAddresses.find(
                            (updatedAddress) =>
                              updatedAddress.data.address.canonical_id === updatedStop?.address?.canonical_id
                          )?.data.address.id ?? undefined,
                      }
                    : updatedStop?.address,
                }))
              ),
            }
          : {}),
      } as ShipperQuoteRequest,
      urlParams: { quoteRequestId: quoteRequest.id },
    }).unwrap();

    setEditing(false);
  };

  const setQuoteRequest = useSetQuoteRequest();
  const setQuoteRequestByPath = useSetQuoteRequestByPath();

  if (!quoteRequest || isLoading) {
    return { isLoading: true };
  }

  const mergedQuoteRequest = merge({}, quoteRequest, quoteRequestUpdates);

  if (!!quoteRequestUpdates.load_spec?.trucks) {
    set(mergedQuoteRequest, "load_spec.trucks", quoteRequestUpdates.load_spec.trucks as Truck[]);
  }

  return {
    isLoading: false,
    quoteRequest: isEditing ? mergedQuoteRequest : quoteRequest,
    quote,
    isEditing,
    setEditing,
    setQuoteRequest,
    setQuoteRequestByPath,
    save: updateQuoteRequest,
  };
};
